import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mail, ArrowRight, Sparkles, Shield, Zap, ThermometerSun, CheckCircle2, ShoppingCart, 
  Sun, Thermometer, Waves, FileText, Wrench, Leaf, Clock, TrendingDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator, BreadcrumbPage } from '../components/ui/breadcrumb';
import SEO from '../components/SEO';
import ContactCTA from '../components/ContactCTA';
import FullWidthHero from '../components/FullWidthHero';
import HelpBlock from '../components/HelpBlock';
import CatalogSearchBar from '../components/CatalogSearchBar';
import { realPhotos, realPhotosAlt } from '../utils/realPhotos';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPrice } from '../utils/formatPrice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL = "contact@atlantiquespaboutique.com";

// Helper to add to cart
const addToCart = (product) => {
  const cart = JSON.parse(localStorage.getItem('spa_cart') || '[]');
  const existingIndex = cart.findIndex(item => item.slug === product.slug);
  
  if (existingIndex >= 0) {
    cart[existingIndex].quantity = (cart[existingIndex].quantity || 1) + 1;
  } else {
    cart.push({
      slug: product.slug,
      name: product.name,
      price: product.price_ttc,
      image: product.images?.[0] || '',
      quantity: 1,
      category: 'pompes-a-chaleur'
    });
  }
  
  localStorage.setItem('spa_cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
  
};

// Product info by power - clear and benefit-oriented
const POWER_INFO = {
  3: {
    climate: 'Climat chaud',
    climateIcon: Sun,
    climateColor: 'text-amber-500',
    climateBg: 'bg-amber-50',
    climateBorder: 'border-amber-200',
    badge: 'Économique',
    badgeColor: 'bg-amber-500',
    shortDesc: 'Adaptée aux régions chaudes ou aux utilisations ponctuelles.',
    longDesc: 'Idéale pour limiter la consommation tout en maintenant une température agréable.',
  },
  5: {
    climate: 'Climat tempéré',
    climateIcon: Thermometer,
    climateColor: 'text-blue-500',
    climateBg: 'bg-blue-50',
    climateBorder: 'border-blue-200',
    badge: 'Populaire',
    badgeColor: 'bg-blue-500',
    shortDesc: 'Solution polyvalente, adaptée à la majorité des situations.',
    longDesc: 'Bon équilibre entre performance, confort et économies d\'énergie.',
  },
  7: {
    climate: 'Climat tempéré',
    climateIcon: Thermometer,
    climateColor: 'text-blue-500',
    climateBg: 'bg-blue-50',
    climateBorder: 'border-blue-200',
    badge: 'Performance',
    badgeColor: 'bg-purple-500',
    shortDesc: 'Recommandée pour les grands volumes d\'eau et les spas de nage.',
    longDesc: 'Offre une montée en température plus rapide et une meilleure stabilité pour les spas de grande capacité.',
  }
};

const PompesChaleurPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/parts?main_category=pompes-a-chaleur`);
        const sorted = response.data.sort((a, b) => (a.power_kw || 0) - (b.power_kw || 0));
        setProducts(sorted);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // JSON-LD for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Pompes à chaleur spa Poolex Spawer O'Spa",
    "description": "Pompes à chaleur Poolex Spawer O'Spa pour spa : 3 kW, 5 kW et 7 kW. Full Inverter, réversible, WiFi intégré.",
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.short_description,
        "brand": { "@type": "Brand", "name": product.brand },
        "offers": {
          "@type": "Offer",
          "price": product.price_ttc,
          "priceCurrency": "EUR",
          "availability": "https://schema.org/InStock"
        }
      }
    }))
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pompes-chaleur-page">
      <SEO 
        title="Pompes à chaleur spa Poolex Spawer O'Spa | 3, 5 et 7 kW | Atlantique Spa Boutique"
        description="Pompes à chaleur spa Poolex Spawer O'Spa (réf. PC-OSPA) 3 kW, 5 kW et 7 kW. Full Inverter, réversible chaud/froid, WiFi intégré. Jusqu'à 75% d'économies d'énergie. Livraison France entière."
        canonical="/pompes-a-chaleur-spa"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Accueil</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/pieces-accessoires">Pièces & Accessoires</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Pompes à chaleur pour spa</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>

      {/* Hero Header - Full Width Image */}
      <FullWidthHero
        variant="compact"
        image={realPhotos.savoirFaire}
        imageAlt={realPhotosAlt.savoirFaire}
        badge="Livraison France"
        badgeIcon={ThermometerSun}
        title="Pompes à chaleur pour spa"
        subtitle="Une pompe à chaleur pour spa permet de chauffer l'eau plus efficacement tout en réduisant significativement la consommation électrique. Elle offre un meilleur confort d'utilisation et une température plus stable."
        overlayPosition="left"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Help Block - visible sans scroll */}
        <div className="mb-8">
          <HelpBlock />
        </div>

        {/* Section: Pourquoi installer une PAC - FORMAT LISTE */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 mb-10 shadow-sm">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-emerald-100 rounded-xl flex-shrink-0">
              <ThermometerSun className="w-7 h-7 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-1">
                Pourquoi installer une pompe à chaleur sur votre spa ?
              </h2>
              <p className="text-slate-500 text-sm">Des avantages concrets au quotidien</p>
            </div>
          </div>
          
          {/* Liste des bénéfices - format clair */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <TrendingDown className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900 text-sm">Réduction significative</span>
                <p className="text-xs text-slate-600 mt-0.5">de la consommation électrique</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <Thermometer className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900 text-sm">Température stable</span>
                <p className="text-xs text-slate-600 mt-0.5">de l'eau en permanence</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-100">
              <Sun className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900 text-sm">Confort toute l'année</span>
                <p className="text-xs text-slate-600 mt-0.5">quelle que soit la saison</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-teal-50 rounded-xl border border-teal-100">
              <Leaf className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900 text-sm">Solution économique</span>
                <p className="text-xs text-slate-600 mt-0.5">sur le long terme</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <Clock className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold text-slate-900 text-sm">Montée en température</span>
                <p className="text-xs text-slate-600 mt-0.5">progressive et efficace</p>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid - Présentation par puissance */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Choisissez votre pompe à chaleur</h2>
          <p className="text-slate-600 mb-6">Sélectionnez le modèle adapté à votre spa et à votre climat</p>
          
          {/* Search Bar */}
          <div className="mb-8">
            <CatalogSearchBar 
              products={products}
              onSearchResults={null}
              placeholder="Rechercher une pièce ou une référence (ex : pompe LX WP250, clavier Balboa, filtre PRB50)"
              category="pompes-a-chaleur"
              emptyIcon={ThermometerSun}
              showHelpText={true}
            />
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse shadow-lg">
                  <div className="h-48 bg-slate-200 rounded-xl mb-4" />
                  <div className="h-6 bg-slate-200 rounded w-16 mb-4" />
                  <div className="h-8 bg-slate-200 rounded w-3/4 mb-4" />
                  <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-6">
              {products.map((product) => {
                const info = POWER_INFO[product.power_kw] || POWER_INFO[5];
                const ClimateIcon = info.climateIcon;
                
                return (
                  <Card 
                    key={product.slug} 
                    className={`relative overflow-hidden border-2 ${info.climateBorder} hover:shadow-xl transition-all duration-300 group bg-white`} 
                    data-testid={`product-${product.slug}`}
                  >
                    {/* Badges */}
                    <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                      {info.badge && (
                        <span className={`inline-flex items-center gap-1 ${info.badgeColor} text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg`}>
                          {info.badge}
                        </span>
                      )}
                    </div>
                    
                    {product.is_new && (
                      <div className="absolute top-4 right-4 z-10">
                        <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-lg">
                          <Sparkles className="w-3 h-3" />
                          Nouveau
                        </span>
                      </div>
                    )}
                    
                    {/* Product Image */}
                    <Link to={`/pompes-a-chaleur-spa/${product.slug}`}>
                      <div className="relative h-52 overflow-hidden bg-slate-100 cursor-pointer">
                        {product.images && product.images.length > 0 ? (
                          <img 
                            src={product.images[0]} 
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-slate-200">
                            <Zap className="w-16 h-16 text-slate-400" />
                          </div>
                        )}
                      </div>
                    </Link>
                    
                    <CardContent className="p-5">
                      {/* Power & Brand */}
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">
                          {product.brand}
                        </span>
                        <div className="flex items-center gap-1.5 text-amber-600 font-bold text-lg">
                          <Zap className="w-5 h-5" />
                          <span>{product.power_kw} kW</span>
                        </div>
                      </div>
                      
                      {/* Title */}
                      <Link to={`/pompes-a-chaleur-spa/${product.slug}`}>
                        <h3 className="text-lg font-bold text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors cursor-pointer">
                          {product.name}
                        </h3>
                      </Link>
                      
                      {/* Climate & Usage Box */}
                      <div className={`${info.climateBg} rounded-lg p-3 mb-4 border ${info.climateBorder}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <ClimateIcon className={`w-4 h-4 ${info.climateColor}`} />
                          <span className={`text-sm font-semibold ${info.climateColor}`}>{info.climate}</span>
                        </div>
                        <p className="text-slate-700 text-sm font-medium mb-1">{info.shortDesc}</p>
                        <p className="text-slate-600 text-xs">{info.longDesc}</p>
                      </div>
                      
                      {/* Price */}
                      <div className="flex items-center justify-between mb-4 pt-3 border-t border-slate-100">
                        <div>
                          <span className="text-2xl font-bold text-slate-900">{formatPrice(product.price_ttc)}</span>
                          <span className="text-sm text-slate-500 ml-1">TTC</span>
                        </div>
                        <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded">
                          En stock
                        </span>
                      </div>
                      
                      {/* Add to Cart Button */}
                      <Button 
                        onClick={() => addToCart(product)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 gap-2"
                        data-testid={`add-to-cart-${product.slug}`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Ajouter au panier
                      </Button>
                      
                      {/* View details link */}
                      <Link 
                        to={`/pompes-a-chaleur-spa/${product.slug}`}
                        className="block text-center text-sm text-slate-500 hover:text-emerald-600 mt-3 transition-colors"
                      >
                        Voir les détails →
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Comparison Table - Simplifié */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 mb-12 shadow-sm">
          <h2 className="text-xl font-bold text-slate-900 mb-6">Quel modèle choisir ?</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Caractéristique</th>
                  <th className="text-center py-3 px-4 font-semibold text-amber-600">O'Spa 3 kW</th>
                  <th className="text-center py-3 px-4 font-semibold text-blue-600">O'Spa 5 kW</th>
                  <th className="text-center py-3 px-4 font-semibold text-purple-600">O'Spa 7 kW</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-slate-600 font-medium">Climat recommandé</td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                      <Sun className="w-4 h-4" /> Chaud
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                      <Thermometer className="w-4 h-4" /> Tempéré
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                      <Thermometer className="w-4 h-4" /> Tempéré
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <td className="py-3 px-4 text-slate-600 font-medium">Usage conseillé</td>
                  <td className="py-3 px-4 text-center text-xs text-slate-600">
                    Régions chaudes<br/>ou usage modéré
                  </td>
                  <td className="py-3 px-4 text-center text-xs text-slate-600">
                    Solution polyvalente<br/>majorité des cas
                  </td>
                  <td className="py-3 px-4 text-center text-xs text-slate-600">
                    Grands volumes<br/>spas de nage
                  </td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-slate-600 font-medium">WiFi intégré</td>
                  <td className="py-3 px-4 text-center"><CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                  <td className="py-3 px-4 text-center"><CheckCircle2 className="w-5 h-5 text-emerald-500 mx-auto" /></td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-slate-600 font-medium">Prix TTC</td>
                  <td className="py-3 px-4 text-center font-bold text-slate-900">399 € TTC</td>
                  <td className="py-3 px-4 text-center font-bold text-slate-900">899 € TTC</td>
                  <td className="py-3 px-4 text-center font-bold text-slate-900">1 099 € TTC</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Positionnement commercial - texte rassurant */}
        <div className="bg-emerald-50 rounded-2xl border border-emerald-200 p-6 md:p-8 mb-12">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-emerald-100 rounded-xl flex-shrink-0">
              <ShoppingCart className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Achat simple et accompagnement disponible</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                Les pompes à chaleur sont <strong>disponibles à l'achat direct</strong> sur notre site. 
                Nous proposons également, si besoin, une <strong>étude personnalisée optionnelle</strong> pour 
                vous aider à choisir le modèle adapté à votre spa et un <strong>accompagnement pour l'installation</strong>.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                    Demander une étude
                  </Button>
                </Link>
                <Link to="/devis">
                  <Button variant="outline" size="sm" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100">
                    Devis installation
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Optional Services Section */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Services complémentaires</h2>
          <p className="text-slate-600 mb-6 text-sm">Optionnels – Non obligatoires pour l'achat</p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Étude personnalisée */}
            <Card className="border-2 border-slate-200 hover:border-blue-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-2">Étude personnalisée</h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Analyse de votre spa, son emplacement et votre usage pour vous conseiller le modèle adapté.
                    </p>
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                        Demander une étude
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Installation */}
            <Card className="border-2 border-slate-200 hover:border-orange-300 transition-colors">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-xl flex-shrink-0">
                    <Wrench className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 mb-2">🚚 Livraison France – offerte dès 80 € TTC</h3>
                    <p className="text-slate-600 text-sm mb-4">
                      Expédition rapide sous 48-72h partout en France. Besoin d'un conseil pour l'installation ? Notre expert vous accompagne par email.
                    </p>
                    <a href={`mailto:${EMAIL}`}>
                      <Button variant="outline" size="sm" className="border-green-300 text-green-600 hover:bg-green-50">
                        Nous contacter
                        <Mail className="w-4 h-4 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Link to blog article */}
        <div className="bg-slate-100 rounded-2xl p-6 mb-12 border border-slate-200">
          <p className="text-sm text-slate-600 mb-3">
            Pour en savoir plus sur les pompes à chaleur pour spa :
          </p>
          <Link 
            to="/blog/pompe-a-chaleur-spa-avantages-rentabilite"
            className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 transition-colors"
          >
            Lire notre guide : Installer une pompe à chaleur sur son spa
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* CTA pièce spécifique */}
        <ContactCTA category="Pompes à chaleur" />

        {/* Contact CTA */}
        <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8f] rounded-2xl p-8 md:p-10 text-center shadow-xl">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Une question sur nos pompes à chaleur ?
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Notre équipe est disponible pour vous conseiller
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={`mailto:${EMAIL}`}>
              <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold shadow-lg gap-2">
                <Mail className="w-5 h-5" />
                Nous contacter
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 font-semibold">
                Envoyer un message
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PompesChaleurPage;
